import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

import Input from '@/components/forms/Input/Input'
import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore from '@/stores/AuthStore'
import routes from '@/routes/routes'

import './VerifyEmail.css'
import '../Auth/Auth.css'
import '@/styles/General.css'

const VerifyEmail = () => {
    const navigate = useNavigate()
    const { 
        verifyConfirmationCode, 
        resendVerificationEmail,
        pendingVerificationEmail,
        isLoading, 
        isLogged,
        getDefaultRoute
    } = useAuthStore()
    const { t } = useTranslation('common')

    const [code, setCode] = useState('')
    const [resendDisabled, setResendDisabled] = useState(false)

    useEffect(() => {
        if (isLogged) {
            // Get user-specific route based on account type
            getDefaultRoute().then(route => {
                navigate(route)
            })
        }
    }, [isLogged, navigate, getDefaultRoute])
    
    useEffect(() => {
        // If no pending verification, redirect to auth
        if (!pendingVerificationEmail) {
            console.log('⚠️ No pending verification email, redirecting to auth');
            navigate(routes.auth);
        }
    }, [pendingVerificationEmail, navigate])

    const validateCode = (code: string) => {
        if (!code || code.length !== 6) {
            return { isValid: false, message: 'Please enter a 6-digit verification code' }
        }
        
        if (!/^\d{6}$/.test(code)) {
            return { isValid: false, message: 'Code must contain only numbers' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault()

        const validation = validateCode(code)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        try {
            const success = await verifyConfirmationCode(code)
            if (success) {
                // clearPendingVerification is called inside verifyConfirmationCode
                // Check if user is now logged in (tokens were returned)
                const { isLogged, getDefaultRoute } = useAuthStore.getState()
                if (isLogged) {
                    console.log('✅ User is now logged in, getting default route...')
                    const defaultRoute = await getDefaultRoute()
                    navigate(defaultRoute)
                } else {
                    console.log('📧 Email confirmed but not logged in, redirecting to auth')
                    navigate(routes.auth)
                }
                return;
            }
        } catch (error) {
            toast.error('Código inválido o expirado')
            console.error('Verification error:', error)
        }
    }
    
    const handleResendCode = async () => {
        setResendDisabled(true);
        const success = await resendVerificationEmail();
        
        if (success) {
            // Disable resend button for 3 minutes
            setTimeout(() => setResendDisabled(false), 180000);
        } else {
            setResendDisabled(false);
        }
    }

    return (
        <div className="auth-page">
            <div className="auth-container">
                {/* Welcome Section */}
                <div className="auth-welcome">
                    <img className="auth-logo-persistent" src="/domber.png" alt="Logo Domber" />
                    
                    <div className="auth-welcome-content">
                        <h1 className="auth-welcome-title auth-form-title">{t('login_verify_email')}</h1>
                        {pendingVerificationEmail && (
                            <p className="auth-welcome-tagline">Código enviado a: {pendingVerificationEmail}</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="auth-footer-global">
                    © 2025 - Domber
                </div>

                {/* Form */}
                <div className="auth-form-container">
                    <div className="auth-card-body">
                        <p className="verify-email-description">Ingresa el código de 6 dígitos enviado a tu correo electrónico.</p>
                        
                        <form className="auth-form" onSubmit={handleSubmit}>
                            <Input
                                name="code"
                                type="text"
                                value={code}
                                setValue={setCode}
                                label="Código de Verificación"
                                placeholder="123456"
                                maxLength={6}
                            />

                            <Button
                                variant="primary"
                                size="lg"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? <Spinner variant="secondary" size="xs" /> : 'Verificar Email'}
                            </Button>
                        </form>
                        
                        <div className="auth-separator"></div>
                        <div className="auth-links">
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={handleResendCode}
                                disabled={resendDisabled || isLoading}
                                className="w-full"
                            >
                                {resendDisabled ? 'Código enviado (espera 3 min)' : '¿No recibiste el código? Reenviar'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default VerifyEmail
